// frontend/src/pages/admin/AdminSettingsPage.jsx
import { useState, useEffect } from 'react';
import { Loader2, Save, Upload, X, Globe, Phone, Mail, MapPin, Instagram, Linkedin, Smartphone } from 'lucide-react';
import { PageSEO } from '../../components/SEO/PageSEO';
import api from '../../services/api';
import config from '../../config';

export const AdminSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [settings, setSettings] = useState({
    // اطلاعات پایه
    site_name: '',
    site_logo: null,
    site_favicon: null,
    site_description: '',
    site_keywords: '',

    // اطلاعات تماس
    phone: '',
    mobile: '',
    email: '',
    address: '',

    // شبکه‌های اجتماعی
    instagram: '',
    telegram: '',
    whatsapp: '',
    linkedin: '',
    twitter: '',
    youtube: '',

    // کدهای tracking
    google_analytics: '',
    google_tag_manager: '',
    footer_scripts: '',

    // تنظیمات سئو
    default_meta_title: '',
    default_meta_description: '',
    default_meta_keywords: '',
    block_search_engines: false,
    robots_txt_content: '',
    site_owner: '',
    site_owner_url: '',
  });

  const [logoPreview, setLogoPreview] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/cms/settings/get_settings/');
      const data = response.data;
      console.log('Settings data:', data);

      setSettings({
        site_name: data.site_name || 'سان ایران',
        site_logo: data.site_logo || null,
        site_favicon: data.site_favicon || null,
        site_description: data.site_description || '',
        site_keywords: data.site_keywords || '',
        phone: data.phone || '',
        mobile: data.mobile || '',
        email: data.email || '',
        address: data.address || '',
        instagram: data.instagram || '',
        telegram: data.telegram || '',
        whatsapp: data.whatsapp || '',
        linkedin: data.linkedin || '',
        twitter: data.twitter || '',
        youtube: data.youtube || '',
        google_analytics: data.google_analytics || '',
        google_tag_manager: data.google_tag_manager || '',
        footer_scripts: data.footer_scripts || '',
        default_meta_title: data.default_meta_title || '',
        default_meta_description: data.default_meta_description || '',
        default_meta_keywords: data.default_meta_keywords || '',
        block_search_engines: data.block_search_engines || false,
        robots_txt_content: data.robots_txt_content || 'User-agent: *\nAllow: /',
        site_owner: data.site_owner || '',
        site_owner_url: data.site_owner_url || '',
      });

      setLogoPreview(data.site_logo ? config.getMediaUrl(data.site_logo) : null);
      setFaviconPreview(data.site_favicon ? config.getMediaUrl(data.site_favicon) : null);

    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (file, fieldName) => {
    const formData = new FormData();
    formData.append(fieldName, file);

    try {
      const accessToken = localStorage.getItem('access_token');
      // استفاده از آدرس درست
      const endpoint = fieldName === 'site_logo' ? '/cms/settings/upload_logo/' : '/cms/settings/upload_favicon/';
      const response = await fetch(config.getApiUrl(endpoint), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (fieldName === 'site_logo') {
          setLogoPreview(data.site_logo);
          setSettings(prev => ({ ...prev, site_logo: data.site_logo }));
        } else if (fieldName === 'site_favicon') {
          setFaviconPreview(data.site_favicon);
          setSettings(prev => ({ ...prev, site_favicon: data.site_favicon }));
        }
        alert('تصویر با موفقیت آپلود شد');
      } else {
        alert('خطا در آپلود تصویر');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('خطا در ارتباط با سرور');
    }
  };


  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('لطفاً فقط فایل تصویر انتخاب کنید');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('حجم تصویر نباید بیشتر از 2 مگابایت باشد');
      return;
    }
    setUploadingLogo(true);
    handleImageUpload(file, 'site_logo').finally(() => setUploadingLogo(false));
  };

  const handleFaviconUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('لطفاً فقط فایل تصویر انتخاب کنید');
      return;
    }
    if (file.size > 500 * 1024) {
      alert('حجم Favicon نباید بیشتر از 500 کیلوبایت باشد');
      return;
    }
    setUploadingFavicon(true);
    handleImageUpload(file, 'site_favicon').finally(() => setUploadingFavicon(false));
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setSaving(true);
  try {
    const accessToken = localStorage.getItem('access_token');
    
    const updateData = {
      site_name: settings.site_name,
      site_description: settings.site_description,
      site_keywords: settings.site_keywords,
      phone: settings.phone,
      mobile: settings.mobile,
      email: settings.email,
      address: settings.address,
      instagram: settings.instagram,
      telegram: settings.telegram,
      whatsapp: settings.whatsapp,
      linkedin: settings.linkedin,
      twitter: settings.twitter,
      youtube: settings.youtube,
      google_analytics: settings.google_analytics,
      google_tag_manager: settings.google_tag_manager,
      footer_scripts: settings.footer_scripts,
      default_meta_title: settings.default_meta_title,
      default_meta_description: settings.default_meta_description,
      default_meta_keywords: settings.default_meta_keywords,
      block_search_engines: settings.block_search_engines,
      robots_txt_content: settings.robots_txt_content,
      site_owner: settings.site_owner,
      site_owner_url: settings.site_owner_url,
    };
    
    // استفاده از آدرس update_settings
    const response = await fetch(config.getApiUrl('/cms/settings/update_settings/'), {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateData)
    });
    
    if (response.ok) {
      alert('تنظیمات با موفقیت ذخیره شد');
      fetchSettings();
    } else {
      const error = await response.json();
      alert('خطا: ' + JSON.stringify(error));
    }
  } catch (error) {
    console.error('Error saving settings:', error);
    alert('خطا در ارتباط با سرور');
  } finally {
    setSaving(false);
  }
};

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <PageSEO title="تنظیمات سایت | پنل ادمین" noindex={true} />

      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">تنظیمات سایت</h1>
            <p className="text-sm text-gray-500 mt-1">مدیریت اطلاعات عمومی، تماس و سئو سایت</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ========== اطلاعات پایه ========== */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">اطلاعات پایه</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">نام سایت</label>
                <input
                  type="text"
                  name="site_name"
                  value={settings.site_name}
                  onChange={handleInputChange}
                  className="w-full max-w-md border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">لوگو</label>
                <div className="flex items-center gap-4">
                  {logoPreview && (
                    <img src={logoPreview} alt="Logo" className="w-20 h-20 object-contain border rounded-lg p-1" />
                  )}
                  <label className="flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer hover:bg-gray-50 transition">
                    <Upload className="w-4 h-4" />
                    <span className="text-sm">{uploadingLogo ? 'در حال آپلود...' : 'آپلود لوگو'}</span>
                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={uploadingLogo} />
                  </label>
                  {settings.site_logo && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (window.confirm('آیا از حذف لوگو اطمینان دارید؟')) {
                          // منطق حذف لوگو
                        }
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1">فرمت‌های مجاز: PNG, JPG, SVG | حداکثر حجم: 2 مگابایت</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Favicon</label>
                <div className="flex items-center gap-4">
                  {faviconPreview && (
                    <img src={faviconPreview} alt="Favicon" className="w-8 h-8 object-contain" />
                  )}
                  <label className="flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer hover:bg-gray-50 transition">
                    <Upload className="w-4 h-4" />
                    <span className="text-sm">{uploadingFavicon ? 'در حال آپلود...' : 'آپلود Favicon'}</span>
                    <input type="file" accept="image/*" onChange={handleFaviconUpload} className="hidden" disabled={uploadingFavicon} />
                  </label>
                </div>
                <p className="text-xs text-gray-400 mt-1">فرمت‌های مجاز: ICO, PNG, SVG | حداکثر حجم: 500 کیلوبایت</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">توضیحات سایت</label>
                <textarea
                  name="site_description"
                  value={settings.site_description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="توضیحات مختصر درباره سایت"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">کلمات کلیدی</label>
                <input
                  type="text"
                  name="site_keywords"
                  value={settings.site_keywords}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="پنل خورشیدی، اینورتر، باتری"
                />
                <p className="text-xs text-gray-400 mt-1">کلمات کلیدی را با کاما جدا کنید</p>
              </div>
            </div>
          </div>

          {/* ========== اطلاعات تماس ========== */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">اطلاعات تماس</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Phone className="w-4 h-4 inline ml-1" />
                  تلفن
                </label>
                <input
                  type="text"
                  name="phone"
                  value={settings.phone}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Smartphone className="w-4 h-4 inline ml-1" />
                  موبایل
                </label>
                <input
                  type="text"
                  name="mobile"
                  value={settings.mobile}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Mail className="w-4 h-4 inline ml-1" />
                  ایمیل
                </label>
                <input
                  type="email"
                  name="email"
                  value={settings.email}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPin className="w-4 h-4 inline ml-1" />
                  آدرس
                </label>
                <input
                  type="text"
                  name="address"
                  value={settings.address}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* ========== شبکه‌های اجتماعی ========== */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">شبکه‌های اجتماعی</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Instagram className="w-4 h-4 inline ml-1" />
                  اینستاگرام
                </label>
                <input
                  type="url"
                  name="instagram"
                  value={settings.instagram}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="https://instagram.com/..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">تلگرام</label>
                <input
                  type="url"
                  name="telegram"
                  value={settings.telegram}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="https://t.me/..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">واتساپ</label>
                <input
                  type="url"
                  name="whatsapp"
                  value={settings.whatsapp}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="https://wa.me/..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Linkedin className="w-4 h-4 inline ml-1" />
                  لینکدین
                </label>
                <input
                  type="url"
                  name="linkedin"
                  value={settings.linkedin}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="https://linkedin.com/..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">توییتر</label>
                <input
                  type="url"
                  name="twitter"
                  value={settings.twitter}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="https://twitter.com/..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">یوتیوب</label>
                <input
                  type="url"
                  name="youtube"
                  value={settings.youtube}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="https://youtube.com/..."
                />
              </div>
            </div>
          </div>

          {/* ========== کدهای Tracking ========== */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">کدهای Tracking</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Google Analytics</label>
                <textarea
                  name="google_analytics"
                  value={settings.google_analytics}
                  onChange={handleInputChange}
                  rows="2"
                  className="w-full font-mono text-sm border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="<!-- Global site tag (gtag.js) - Google Analytics -->"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Google Tag Manager</label>
                <textarea
                  name="google_tag_manager"
                  value={settings.google_tag_manager}
                  onChange={handleInputChange}
                  rows="2"
                  className="w-full font-mono text-sm border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="<!-- Google Tag Manager -->"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">اسکریپت‌های فوتر</label>
                <textarea
                  name="footer_scripts"
                  value={settings.footer_scripts}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full font-mono text-sm border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="کدهای اضافی که در فوتر سایت قرار می‌گیرد"
                />
              </div>
            </div>
          </div>

          {/* ========== تنظیمات سئو ========== */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">
              <Globe className="w-5 h-5 inline ml-1" />
              تنظیمات سئو
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">متا تایتل پیش‌فرض</label>
                <input
                  type="text"
                  name="default_meta_title"
                  value={settings.default_meta_title}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  maxLength="150"
                />
                <p className="text-xs text-gray-500 mt-1">{settings.default_meta_title.length}/150 کاراکتر</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">متا دیسکریپشن پیش‌فرض</label>
                <textarea
                  name="default_meta_description"
                  value={settings.default_meta_description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  maxLength="300"
                />
                <p className="text-xs text-gray-500 mt-1">{settings.default_meta_description.length}/300 کاراکتر</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">کلمات کلیدی پیش‌فرض</label>
                <input
                  type="text"
                  name="default_meta_keywords"
                  value={settings.default_meta_keywords}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="پنل خورشیدی، اینورتر، باتری"
                />
              </div>

              <div className="bg-yellow-50 rounded-lg p-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="block_search_engines"
                    checked={settings.block_search_engines}
                    onChange={handleInputChange}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">مسدود کردن موتورهای جستجو (noindex)</span>
                </label>
                <p className="text-xs text-gray-500 mt-1">با فعال کردن این گزینه، کل سایت از ایندکس شدن توسط گوگل مسدود می‌شود</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">محتوای robots.txt</label>
                <textarea
                  name="robots_txt_content"
                  value={settings.robots_txt_content}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full font-mono text-sm border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-400 mt-1">در صورت خالی بودن، مقدار پیش‌فرض استفاده می‌شود</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">مالک سایت</label>
                  <input
                    type="text"
                    name="site_owner"
                    value={settings.site_owner}
                    onChange={handleInputChange}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">آدرس مالک سایت</label>
                  <input
                    type="url"
                    name="site_owner_url"
                    value={settings.site_owner_url}
                    onChange={handleInputChange}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* دکمه ذخیره */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'در حال ذخیره...' : 'ذخیره تنظیمات'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};
